import { useEffect } from 'react';

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import type {
  AgentTemplate,
  AgentTemplateWizardStepType,
  WizardStepProps,
} from '@/features/agents/types';
import { useListVoicesQuery } from '../api/numbersApi';
import { isForwardingStepValue } from '../types';
import type { ForwardingStepValue, TemplateForwardingCfg } from '../types';

/**
 * `forwarding` wizard step renderer (design §12.1, P2 §14.4 contract).
 * Contributed by the numbers feature and picked up by the P2 glob step
 * registry (`<feature>/wizard-steps/<name>.step.tsx` under src/features) —
 * zero edits to P2 files.
 *
 * Value persistence follows the frame: non-attach types store their value in
 * `draft.steps[entry.key]` via `value`/`onChange` (P2 `draft.ts`); the draft
 * mirrors `TemplateForwardingCfg`. Merging it into the create call's
 * `overrides.forwarding` (§15.2, OQ-3) is consumed P2-side — a config value,
 * not a deferred attach.
 */

const DEFAULTS_FALLBACK: ForwardingStepValue = {
  enabled: false,
  audioOut: { enabled: false },
  greeting: '',
};

function SwitchRow({
  label,
  helper,
  checked,
  disabled,
  onToggle,
}: {
  label: string;
  helper: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={onToggle}
        className={`mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-50 ${
          checked ? 'border-primary bg-primary' : 'border-input bg-input'
        }`}
      >
        <span
          aria-hidden="true"
          className={`block h-4 w-4 rounded-full bg-background transition-transform motion-reduce:transition-none ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
      <div className="space-y-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-sm text-muted-foreground">{helper}</p>
      </div>
    </div>
  );
}

function ForwardingStep({ template, value, onChange, errors, onErrors }: WizardStepProps) {
  const cfg = (template as AgentTemplate & { forwarding?: TemplateForwardingCfg }).forwarding;

  // Until the user touches the step the value lives in the template defaults
  // (same posture as the P2 attach-storage step).
  const state: ForwardingStepValue = isForwardingStepValue(value)
    ? value
    : cfg
      ? {
          enabled: cfg.enabled === true,
          audioOut: { enabled: cfg.audioOut?.enabled === true, voiceId: cfg.audioOut?.voiceId },
          greeting: cfg.greeting ?? '',
        }
      : DEFAULTS_FALLBACK;

  const defaultVoiceId = cfg?.audioOut?.voiceId;
  const voicesQuery = useListVoicesQuery(undefined, {
    skip: !state.enabled || !state.audioOut.enabled,
  });
  // Owner-scoped list (audit #8): only active, provider-id-carrying voices.
  const voices = (voicesQuery.data ?? []).filter(
    (voice) => voice.isActive !== false && voice.providerVoiceId,
  );
  const selectedIsDefault = state.audioOut.voiceId === defaultVoiceId && Boolean(defaultVoiceId);
  const pinTemplateDefault =
    Boolean(defaultVoiceId) && !voices.some((v) => v.providerVoiceId === defaultVoiceId);

  const missingVoice = state.enabled && state.audioOut.enabled && !state.audioOut.voiceId;

  // Continue gating rides the P2 frame contract: non-empty step errors disable
  // Continue (AgentWizardPage stepGate). The design's per-step tooltip copy is
  // surfaced inline next to the field instead (D5) — the frame's tooltip text
  // is fixed "Fix the highlighted fields to continue."
  useEffect(() => {
    onErrors(missingVoice ? { voice: 'Select a voice to continue.' } : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onErrors identity
    // changes per frame render; the value is stable per `missingVoice` flip.
  }, [missingVoice]);

  const forwardingDisabled = !state.enabled;

  return (
    <div className="space-y-6">
      <SwitchRow
        label="Enable call forwarding"
        helper="Calls to the agent's number are answered by the agent."
        checked={state.enabled}
        onToggle={() => onChange({ ...state, enabled: !state.enabled })}
      />

      {forwardingDisabled ? (
        <p className="text-sm text-muted-foreground">
          Turn on call forwarding to configure spoken replies.
        </p>
      ) : null}

      <div className="space-y-4">
        <SwitchRow
          label="Spoken replies"
          helper="The agent speaks replies using the selected voice."
          checked={state.audioOut.enabled}
          disabled={forwardingDisabled}
          onToggle={() =>
            onChange({
              ...state,
              audioOut: { ...state.audioOut, enabled: !state.audioOut.enabled },
            })
          }
        />

        {state.audioOut.enabled ? (
          <div className="space-y-2">
            <Label htmlFor="forwarding-voice">Voice</Label>
            <Select
              value={state.audioOut.voiceId ?? ''}
              onValueChange={(voiceId) =>
                onChange({ ...state, audioOut: { ...state.audioOut, voiceId } })
              }
              disabled={forwardingDisabled || voicesQuery.isLoading}
            >
              <SelectTrigger id="forwarding-voice" className="max-w-sm">
                <SelectValue
                  placeholder={voicesQuery.isLoading ? 'Loading voices…' : 'Select a voice'}
                />
              </SelectTrigger>
              <SelectContent>
                {/* Pinned default keeps the select alive even when the
                    owner-scoped voice list is empty (audit #8/B3). */}
                {selectedIsDefault || pinTemplateDefault ? (
                  <SelectItem value={defaultVoiceId as string}>
                    <span title={defaultVoiceId}>
                      Template default —{' '}
                      <span className="font-mono text-xs">
                        {defaultVoiceId && defaultVoiceId.length > 18
                          ? `${defaultVoiceId.slice(0, 18)}…`
                          : defaultVoiceId}
                      </span>
                    </span>
                  </SelectItem>
                ) : null}
                {voices.map((voice) => (
                  <SelectItem key={voice.id} value={voice.providerVoiceId as string}>
                    {voice.name ?? voice.providerVoiceId}
                    {voice.language ? ` — ${voice.language}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Only voices you've uploaded are listed; the template default is used otherwise.
            </p>
            {voicesQuery.isError ? (
              <p className="flex items-center gap-2 text-sm">
                Couldn't load voices.
                <Button variant="outline" size="sm" onClick={() => void voicesQuery.refetch()}>
                  Retry
                </Button>
              </p>
            ) : null}
            {errors.voice ? (
              <p
                role="alert"
                className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-foreground"
              >
                {errors.voice}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="forwarding-greeting">Greeting</Label>
        <Textarea
          id="forwarding-greeting"
          rows={3}
          value={state.greeting}
          onChange={(event) => onChange({ ...state, greeting: event.target.value })}
          disabled={forwardingDisabled}
        />
        <p className="text-sm text-muted-foreground">Spoken when the agent answers a call.</p>
      </div>
    </div>
  );
}

export const type: AgentTemplateWizardStepType = 'forwarding';
export const component = ForwardingStep;
